import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import PhoneNumberInput, {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'react-phone-number-input/input';
import type { Country } from 'react-phone-number-input';
import countryNames from 'react-phone-number-input/locale/en.json';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetch } from '@/hooks/use-fetch';
import type { AlertPhoneStatus } from '@/pages/launchpad/use-alert-phone';

/** "US +1" in the trigger, "United States +1" in the list — sorted by name. */
export const COUNTRY_ITEMS = getCountries()
  .map((c) => ({
    value: c as string,
    label: `${c} +${getCountryCallingCode(c)}`,
    name: (countryNames as Record<string, string>)[c] ?? c,
    callingCode: getCountryCallingCode(c),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * The browser's region beats a hardcoded 'US': most people type their national
 * number and never touch the picker, and a mismatched country silently
 * disables the submit button (an Israeli 054… is not a valid US number).
 */
export function defaultCountry(): Country {
  const region = new Intl.Locale(navigator.language).maximize().region;
  const known = new Set<string>(getCountries());
  return (region && known.has(region) ? region : 'US') as Country;
}

/**
 * AIT-370 — capture + verify the user's private alert phone. Two stages:
 * number + channel choice → 6-digit code. A dismissal is a normal outcome;
 * nothing downstream depends on completing this. Verifying the number is the
 * consent signature for all alert categories (AIT-383) — the disclosure under
 * the form says so; opt-out lives in consent PATCH via CLI/MCP or removal.
 *
 * AIT-494 split the content out as AlertPhoneForm so the add-card dialog can
 * embed the same capture+verify flow as its second step instead of stacking a
 * second dialog on the customer.
 */
export function AlertPhoneDialog({
  open,
  onOpenChange,
  onVerified,
  current,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: () => void;
  /** Existing status — the Change flow seeds its controls from it. */
  current?: AlertPhoneStatus | null;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else onOpenChange(false);
      }}
    >
      <DialogContent className="sm:max-w-md">
        {open && (
          <AlertPhoneForm
            current={current}
            onVerified={onVerified}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The capture+verify flow without dialog chrome — embed anywhere. Renders its
 *  own DialogHeader (stage-dependent title), so the host DialogContent should
 *  not add one. Mount fresh per open (state resets by unmount). */
export function AlertPhoneForm({
  current,
  onVerified,
  onClose,
  skipLabel = 'Not now',
}: {
  current?: AlertPhoneStatus | null;
  onVerified: () => void;
  /** Called for both a skip and a completed verify — the host closes itself. */
  onClose: () => void;
  skipLabel?: string;
}) {
  const fetchWithAuth = useFetch();
  const [stage, setStage] = useState<'number' | 'code'>('number');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<Country>(defaultCountry);
  const [channel, setChannel] = useState<'whatsapp' | 'sms' | 'both'>('whatsapp');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const countryName = COUNTRY_ITEMS.find((c) => c.value === country)?.name ?? country;
  // Bumped on every close. An in-flight submit captures the current value and
  // drops its result if the dialog was dismissed meanwhile — otherwise a late
  // send could flip a closed dialog to the code stage, and the next open would
  // start mid-verification for a request the user abandoned.
  const generation = useRef(0);

  useEffect(() => {
    if (!current?.verified) return;
    const pref = current.channelPreference;
    setChannel(pref === 'sms' || pref === 'both' ? pref : 'whatsapp');
  }, [current]);

  const close = () => {
    generation.current += 1;
    reset();
    onClose();
  };

  const reset = () => {
    setStage('number');
    setCode('');
    setError(null);
    setBusy(false);
  };

  const submitNumber = async () => {
    const gen = generation.current;
    setBusy(true);
    setError(null);
    try {
      const res = await fetchWithAuth('/auth/phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          // AIT-383: verifying the number is the consent signature for all
          // categories — the small print below the form states exactly that.
          // Matches the CLI and the MCP tool defaults.
          consentOperational: true,
          consentProduct: true,
          consentMarketing: true,
          channelPreference: channel,
        }),
      });
      const body = (await res.json()) as { delivery?: string; message?: string };
      // Dialog dismissed while the request was in flight — ignore its result.
      if (gen !== generation.current) return;
      if (!res.ok) {
        setError(body.message ?? 'Something went wrong. Check the number and try again.');
        return;
      }
      if (body.delivery !== 'sent') {
        // Nothing was delivered, and the backend supersedes the challenge on a
        // failed send — so a code stage here would ask for a code that does not
        // exist and could never verify. Stay on the number stage, same as the
        // CLI does.
        setError('We could not send a code to that number right now. Try again in a moment.');
        return;
      }
      setStage('code');
    } catch {
      if (gen !== generation.current) return;
      setError('Something went wrong. Check the number and try again.');
    } finally {
      if (gen === generation.current) setBusy(false);
    }
  };

  const submitCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetchWithAuth('/auth/phone/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const body = (await res.json()) as { message?: string };
      if (!res.ok) {
        setError(body.message ?? 'That code is not correct.');
        return;
      }
      onVerified();
      close();
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
        {stage === 'number' ? (
          <>
            <DialogHeader>
              <DialogTitle>Get alerts if something breaks</DialogTitle>
              <DialogDescription>
                If something ever stops working we will let you know!
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="alert-phone-number">Phone number</Label>
                <div className="flex items-center gap-2">
                  <Select
                    items={COUNTRY_ITEMS}
                    value={country as string}
                    onValueChange={(v) => {
                      // Clearing the number on country change stops a leftover
                      // national number from silently re-keying to a new
                      // calling code (+1 555… becoming +44 555…).
                      setCountry((v ?? 'US') as Country);
                      setPhone('');
                    }}
                  >
                    <SelectTrigger aria-label="Country" className="w-28 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent
                      // 240 countries: aligning the SELECTED item over the
                      // trigger throws the popup far off-screen. Anchor it to
                      // the trigger like a normal dropdown instead.
                      alignItemWithTrigger={false}
                      align="start"
                      className="max-h-72 min-w-64"
                    >
                      {COUNTRY_ITEMS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.name} +{c.callingCode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <PhoneNumberInput
                    id="alert-phone-number"
                    country={country}
                    value={phone}
                    onChange={(v) => setPhone(v ?? '')}
                    inputComponent={Input}
                    autoComplete="tel"
                  />
                </div>
                {phone && !isValidPhoneNumber(phone) && (
                  // Without this the submit button is just dead — the usual
                  // cause is a national number typed under the wrong country.
                  <p className="text-sm text-muted-foreground">
                    That is not a valid {countryName} number. Check the country next to it.
                  </p>
                )}
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Send the code and alerts by</legend>
                <div className="flex gap-4" role="radiogroup" aria-label="Delivery channel">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="alert-phone-channel"
                      checked={channel === 'whatsapp'}
                      onChange={() => setChannel('whatsapp')}
                    />
                    WhatsApp
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="alert-phone-channel"
                      checked={channel === 'sms'}
                      onChange={() => setChannel('sms')}
                    />
                    SMS
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="alert-phone-channel"
                      checked={channel === 'both'}
                      onChange={() => setChannel('both')}
                    />
                    Both
                  </label>
                </div>
              </fieldset>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">
                By adding your number, you consent to us contacting you when anything
                breaks or fails, as well as with product news and occasional marketing.
                Opt out anytime.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                {skipLabel}
              </Button>
              <Button onClick={submitNumber} disabled={busy || !phone || !isValidPhoneNumber(phone)}>
                {/* The request now waits on a real delivery check against the
                    provider, so it is slow enough to need a spinner. */}
                {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                {busy ? 'Sending…' : 'Send code'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Enter the code we sent</DialogTitle>
              <DialogDescription>
                We texted a 6-digit code to {phone}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="alert-phone-code">Verification code</Label>
              <Input
                id="alert-phone-code"
                placeholder="123456"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Later
              </Button>
              <Button onClick={submitCode} disabled={busy || !/^\d{6}$/.test(code)}>
                {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                {busy ? 'Verifying…' : 'Verify'}
              </Button>
            </DialogFooter>
          </>
        )}
    </>
  );
}
